import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import SyncAllModal from '../components/SyncAllModal';
import { api } from '../lib/api';

interface DashboardData {
  salesOrders: {
    counts: {
      draft: number;
      offered: number;
      accepted: number;
      delivered: number;
      totalValue: number;
      totalBuildCost: number;
    };
    recentAccepted: Array<{
      id: string;
      orderNumber: number;
      customer: string | null;
      priceAccepted: number | null;
      buildCost: number | null;
      updatedAt: string;
    }>;
  };
  supplyChain: {
    totalItems: number;
    itemsWithNetNeed: number;
    buildItems: number;
    buyItems: number;
    lastCalculatedAt: string | null;
  };
  industryJobs: {
    activeCount: number;
    completingSoonCount: number;
    upcomingJobs: Array<{
      jobId: number;
      activityId: number;
      activityName: string;
      productTypeId: number | null;
      productName: string | null;
      runs: number;
      endDate: string;
      characterName: string;
    }>;
  };
  buyOrders: {
    totalCount: number;
    totalEscrow: number;
    topOrders: Array<{
      id: string;
      typeId: number;
      typeName: string;
      price: number;
      volumeRemain: number;
      volumeTotal: number;
      escrow: number;
      characterName: string;
    }>;
  };
  shoppingListPreview: Array<{
    typeId: number;
    typeName: string;
    netQuantity: number;
  }>;
}

function formatISK(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  if (value >= 1e9) return (value / 1e9).toFixed(2) + ' B';
  if (value >= 1e6) return (value / 1e6).toFixed(2) + ' M';
  if (value >= 1e3) return (value / 1e3).toFixed(2) + ' K';
  return value.toFixed(2);
}

function formatNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }
  return num.toLocaleString();
}

function formatTimeRemaining(endDateString: string): string {
  const endDate = new Date(endDateString);
  const now = new Date();
  const diffMs = endDate.getTime() - now.getTime();

  if (diffMs <= 0) return 'Ready';

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) {
    const mins = diffMins % 60;
    return `${diffHours}h ${mins}m`;
  }

  const days = Math.floor(diffHours / 24);
  const hours = diffHours % 24;
  return `${days}d ${hours}h`;
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Get the correct EVE image type based on activity
// Activity 3/4 = ME/TE Research (produces BPO), Activity 5 = Copying, Activity 8 = Invention (produces BPC)
function getJobImageType(activityId: number): 'icon' | 'bp' | 'bpc' {
  if (activityId === 3 || activityId === 4) return 'bp';  // ME/TE Research produces BPO
  if (activityId === 5 || activityId === 8) return 'bpc'; // Copying/Invention produces BPC
  return 'icon'; // Manufacturing/Reactions produce items
}

function Home() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSyncModal, setShowSyncModal] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get('/dashboard');
        setData(response.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400">Loading dashboard...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-red-400">{error}</div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400">No data available</div>
        </div>
      </Layout>
    );
  }

  const margin = data.salesOrders.counts.totalValue - data.salesOrders.counts.totalBuildCost;
  const marginPercent = data.salesOrders.counts.totalBuildCost > 0
    ? ((margin / data.salesOrders.counts.totalBuildCost) * 100).toFixed(1)
    : '0';

  return (
    <Layout>
      <div className="h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <button
            onClick={() => setShowSyncModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Sync All
          </button>
        </div>

        <SyncAllModal isOpen={showSyncModal} onClose={() => setShowSyncModal(false)} />

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Sales Orders Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-400 text-sm font-medium">Sales Orders</h3>
              <Link to="/sales-orders" className="text-blue-400 hover:text-blue-300 text-xs">
                View all
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                <span className="text-gray-400">Draft:</span>
                <span className="text-white font-medium">{data.salesOrders.counts.draft}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                <span className="text-gray-400">Offered:</span>
                <span className="text-white font-medium">{data.salesOrders.counts.offered}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-400">Accepted:</span>
                <span className="text-white font-medium">{data.salesOrders.counts.accepted}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-gray-400">Delivered:</span>
                <span className="text-white font-medium">{data.salesOrders.counts.delivered}</span>
              </div>
            </div>
          </div>

          {/* Accepted Orders Value Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <h3 className="text-gray-400 text-sm font-medium mb-3">Accepted Orders Value</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400 text-sm">Revenue:</span>
                <span className="text-green-400 font-medium">{formatISK(data.salesOrders.counts.totalValue)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400 text-sm">Build Cost:</span>
                <span className="text-yellow-400 font-medium">{formatISK(data.salesOrders.counts.totalBuildCost)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-700 pt-2">
                <span className="text-gray-400 text-sm">Margin:</span>
                <span className={`font-medium ${margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatISK(margin)} ({marginPercent}%)
                </span>
              </div>
            </div>
          </div>

          {/* Supply Chain Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-400 text-sm font-medium">Supply Chain</h3>
              <Link to="/supply-chain" className="text-blue-400 hover:text-blue-300 text-xs">
                View all
              </Link>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Total Items:</span>
                <span className="text-white font-medium">{data.supplyChain.totalItems}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Need Attention:</span>
                <span className={`font-medium ${data.supplyChain.itemsWithNetNeed > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                  {data.supplyChain.itemsWithNetNeed}
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 pt-1">
                <span>Build: {data.supplyChain.buildItems}</span>
                <span>Buy: {data.supplyChain.buyItems}</span>
              </div>
            </div>
            {data.supplyChain.lastCalculatedAt && (
              <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-700">
                Updated: {formatTimeAgo(data.supplyChain.lastCalculatedAt)}
              </div>
            )}
          </div>

          {/* Industry Jobs Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-400 text-sm font-medium">Industry Jobs</h3>
              <Link to="/industry-jobs" className="text-blue-400 hover:text-blue-300 text-xs">
                View all
              </Link>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Active Jobs:</span>
                <span className="text-white font-medium">{data.industryJobs.activeCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Completing in 24h:</span>
                <span className={`font-medium ${data.industryJobs.completingSoonCount > 0 ? 'text-blue-400' : 'text-gray-500'}`}>
                  {data.industryJobs.completingSoonCount}
                </span>
              </div>
            </div>
          </div>

          {/* Buy Orders Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-gray-400 text-sm font-medium">Buy Orders</h3>
              <Link to="/buy-orders" className="text-blue-400 hover:text-blue-300 text-xs">
                View all
              </Link>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Active Orders:</span>
                <span className="text-white font-medium">{data.buyOrders.totalCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Total Escrow:</span>
                <span className="text-purple-400 font-medium">{formatISK(data.buyOrders.totalEscrow)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Recent Accepted Orders */}
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-white font-medium">Recent Accepted Orders</h2>
                <Link to="/sales-orders" className="text-blue-400 hover:text-blue-300 text-sm">
                  View all
                </Link>
              </div>
              {data.salesOrders.recentAccepted.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No accepted orders
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {data.salesOrders.recentAccepted.map(order => (
                    <Link
                      key={order.id}
                      to={`/sales-orders/${order.orderNumber}`}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-750 transition-colors"
                    >
                      <div>
                        <div className="text-white text-sm font-medium">
                          Order #{order.orderNumber}
                        </div>
                        {order.customer && (
                          <div className="text-gray-400 text-xs">{order.customer}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-green-400 text-sm font-medium">
                          {formatISK(order.priceAccepted)}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {formatTimeAgo(order.updatedAt)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping List Preview */}
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-white font-medium">Top Items to Buy</h2>
                <Link to="/supply-chain" className="text-blue-400 hover:text-blue-300 text-sm">
                  Full list
                </Link>
              </div>
              {data.shoppingListPreview.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No items need to be purchased
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {data.shoppingListPreview.map(item => (
                    <div
                      key={item.typeId}
                      className="flex items-center gap-3 px-4 py-2"
                    >
                      <img
                        src={`https://images.evetech.net/types/${item.typeId}/icon?size=32`}
                        alt=""
                        className="w-6 h-6"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <span className="flex-1 text-gray-200 text-sm truncate">
                        {item.typeName}
                      </span>
                      <span className="text-yellow-400 text-sm font-medium">
                        {formatNumber(item.netQuantity)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Upcoming Jobs */}
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-white font-medium">Jobs Completing Soon</h2>
                <Link to="/industry-jobs" className="text-blue-400 hover:text-blue-300 text-sm">
                  View all
                </Link>
              </div>
              {data.industryJobs.upcomingJobs.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No jobs completing in the next 24 hours
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {data.industryJobs.upcomingJobs.map(job => (
                    <div
                      key={job.jobId}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      {job.productTypeId && (
                        <img
                          src={`https://images.evetech.net/types/${job.productTypeId}/${getJobImageType(job.activityId)}?size=32`}
                          alt=""
                          className="w-8 h-8"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-200 text-sm truncate">
                          {job.productName || job.activityName}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {job.characterName} - {job.runs} runs
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${
                          formatTimeRemaining(job.endDate) === 'Ready' ? 'text-green-400' : 'text-blue-400'
                        }`}>
                          {formatTimeRemaining(job.endDate)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {job.activityName}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Buy Orders */}
            <div className="bg-gray-800 rounded-lg border border-gray-700">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-white font-medium">Top Buy Orders</h2>
                <Link to="/buy-orders" className="text-blue-400 hover:text-blue-300 text-sm">
                  View all
                </Link>
              </div>
              {data.buyOrders.topOrders.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No active buy orders
                </div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {data.buyOrders.topOrders.map(order => (
                    <div
                      key={order.id}
                      className="flex items-center gap-3 px-4 py-3"
                    >
                      <img
                        src={`https://images.evetech.net/types/${order.typeId}/icon?size=32`}
                        alt=""
                        className="w-8 h-8"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-200 text-sm truncate">
                          {order.typeName}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {order.characterName} - {order.volumeRemain}/{order.volumeTotal}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-purple-400 text-sm font-medium">
                          {formatISK(order.escrow)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatISK(order.price)}/unit
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Home;
